import * as c from 'common/c'

/**
 * Numeric coercion that treats only numbers and non-blank numeric strings as
 * usable. Plain `Number()` is too permissive here: `Number(null)` and
 * `Number('')` are both 0, which is finite, so a missing value would be read as
 * a real zero and clamped to the minimum instead of falling back to a default.
 */
const toNumber = (value: unknown): number => {
  if (typeof value === 'number') return value
  if (typeof value === 'string' && value.trim() !== '') return Number(value)
  return NaN
}

/**
 * Clamp a vertical exaggeration factor into the range the 3D scene accepts.
 *
 * Applied both in the action creator and again before the value reaches
 * Cesium, so a dispatch from anywhere cannot wedge the globe at an absurd
 * scale. Non-finite input falls back to the default rather than to the minimum:
 * a NaN means something upstream is broken, and quietly flattening the terrain
 * hides that, whereas the default keeps the view usable.
 */
export const clampExaggeration = (value: unknown): number => {
  const numeric = toNumber(value)
  if (!Number.isFinite(numeric)) return c.DEFAULT_VERTICAL_EXAGGERATION
  return Math.min(
    c.MAX_VERTICAL_EXAGGERATION,
    Math.max(c.MIN_VERTICAL_EXAGGERATION, numeric),
  )
}

/**
 * The height that vertical exaggeration is applied around.
 *
 * Cesium's `verticalExaggerationRelativeHeight` defaults to 0, i.e. the
 * ellipsoid, which means exaggerating a field sitting at 300 m lifts the whole
 * field to 900 m at 3x instead of revealing any of its relief. Pivoting at the
 * field's own elevation keeps it where the camera already is and exaggerates
 * the relief symmetrically about it.
 *
 * Returns 0 for a missing or unusable sample. That reproduces Cesium's own
 * default — the field will climb away from the camera as exaggeration rises —
 * but it is a predictable degradation rather than a crash, and it only happens
 * when terrain sampling has already failed.
 */
export const relativeHeightFor = (sampledHeight: unknown): number => {
  const numeric = toNumber(sampledHeight)
  return Number.isFinite(numeric) ? numeric : 0
}
